/**
 * backendOrigin — Resolves the backend API base URL at runtime.
 *
 * Priority:
 *   1. Use REACT_APP_BACKEND_ORIGIN env var if set AND either:
 *      - it is not a localhost/127.0.0.1 URL (i.e. a real production URL), OR
 *      - the app itself is also running locally (development machine)
 *   2. Otherwise fall back to http://<current-hostname>:4000
 *      (handles LAN deployments where the backend runs on the same machine)
 */
const LOCAL_HOSTS = ['localhost', '127.0.0.1']

const configuredOrigins = (process.env.REACT_APP_BACKEND_ORIGIN || '')
    .split(',')
    .map((origin) => origin.trim())
    .filter(Boolean)

const getRuntimeHostname = () => {
    if (typeof window === 'undefined') {
        return 'localhost'
    }

    return window.location.hostname || 'localhost'
}

const normalizeConfiguredOrigin = (origin) => {
    try {
        const parsed = new URL(origin)

        // Guard against accidentally supplying frontend dev-server origins.
        if (parsed.port === '3000') {
            parsed.port = '4000'
        }

        return parsed.toString().replace(/\/$/, '')
    } catch (error) {
        return ''
    }
}

const normalizedConfiguredOrigins = configuredOrigins
    .map(normalizeConfiguredOrigin)
    .filter(Boolean)

const runtimeHostname = getRuntimeHostname()
const isRuntimeLocal = LOCAL_HOSTS.includes(runtimeHostname)

const selectConfiguredOrigin = () => {
    if (!normalizedConfiguredOrigins.length) {
        return ''
    }

    const exactHostMatch = normalizedConfiguredOrigins.find((origin) => {
        try {
            return new URL(origin).hostname === runtimeHostname
        } catch (error) {
            return false
        }
    })

    if (exactHostMatch) {
        return exactHostMatch
    }

    if (isRuntimeLocal) {
        const localMatch = normalizedConfiguredOrigins.find((origin) => {
            try {
                return LOCAL_HOSTS.includes(new URL(origin).hostname)
            } catch (error) {
                return false
            }
        })

        if (localMatch) {
            return localMatch
        }
    }

    return normalizedConfiguredOrigins[0]
}

export const backendOrigin = selectConfiguredOrigin() || `http://${runtimeHostname}:4000`

export default backendOrigin